// Cálculo dos indicadores da dashboard a partir das listas já retornadas pelo back.
// Funções puras: recebem os dados brutos e devolvem os números/séries prontos para exibir.

const MESES = ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez'];
const DIA_MS = 24 * 60 * 60 * 1000;

const toDate = (valor) => {
    if (!valor) return null;
    const d = new Date(valor);
    return Number.isNaN(d.getTime()) ? null : d;
};

const chaveMes = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;

// Últimos `n` meses (inclui o mês atual), do mais antigo para o mais recente.
export const ultimosMeses = (n, hoje = new Date()) =>
    Array.from({ length: n }, (_, i) => {
        const d = new Date(hoje.getFullYear(), hoje.getMonth() - (n - 1 - i), 1);
        return { chave: chaveMes(d), label: `${MESES[d.getMonth()]}/${String(d.getFullYear()).slice(2)}` };
    });

const contarPorMes = (itens, getData, meses) => {
    const contagem = Object.fromEntries(meses.map((m) => [m.chave, 0]));
    itens.forEach((item) => {
        const d = toDate(getData(item));
        if (!d) return;
        const chave = chaveMes(d);
        if (chave in contagem) contagem[chave] += 1;
    });
    return meses.map((m) => contagem[m.chave]);
};

const contarPor = (itens, getChave) =>
    itens.reduce((acc, item) => {
        const chave = getChave(item) || 'Não informado';
        acc[chave] = (acc[chave] || 0) + 1;
        return acc;
    }, {});

// Converte { chave: qtd } em [{ label, value }] ordenado do maior para o menor.
export const paraLista = (obj, formatarLabel = (k) => k) =>
    Object.entries(obj)
        .map(([chave, value]) => ({ label: formatarLabel(chave), value }))
        .sort((a, b) => b.value - a.value);

const STATUS_ATIVA = 'ACTIVE';
const isAtiva = (a) => String(a?.status || '').toUpperCase() === STATUS_ATIVA;

// Valor mensal equivalente da assinatura com base no preço atual do plano.
const valorMensalAssinatura = (assinatura, planosPorId) => {
    const plano = planosPorId[assinatura?.idPlano];
    if (!plano) return 0;
    if (String(assinatura?.periodicidade || '').toUpperCase() === 'ANUAL') {
        return Number(plano.valorNovoAnual || 0) / 12 || Number(plano.valorNovoMensal || 0);
    }
    return Number(plano.valorNovoMensal || 0);
};

const METODOS_PAGAMENTO = { BOLETO: 'Boleto', PIX: 'Pix', CREDIT_CARD: 'Cartão de crédito', CARTAO: 'Cartão de crédito', UNDEFINED: 'Não definido' };
const formatarMetodo = (m) => METODOS_PAGAMENTO[String(m).toUpperCase()] || m;
const formatarPeriodicidade = (p) => ({ MENSAL: 'Mensal', ANUAL: 'Anual' }[String(p).toUpperCase()] || p);

export function calcularMetricas(dados, hoje = new Date()) {
    const assinaturas = dados.assinaturas || [];
    const planos = dados.planos || [];
    const cancelamentos = dados.cancelamentos || [];
    const clientes = dados.clientes || [];
    const leadsLanding = dados.leadsLanding || [];
    const leadsPerdidos = dados.leadsPerdidos || [];

    const planosPorId = Object.fromEntries(planos.map((p) => [p.idPlano, p]));
    const ativas = assinaturas.filter(isAtiva);
    const pendentes = assinaturas.filter((a) => String(a?.status || '').toUpperCase() === 'PENDENTE');

    // Receita recorrente mensal estimada (preço atual do plano, sem considerar descontos).
    const mrr = ativas.reduce((acc, a) => acc + valorMensalAssinatura(a, planosPorId), 0);
    const ticketMedio = ativas.length ? mrr / ativas.length : 0;

    const nomePlano = (a) => a?.Plano?.nome || a?.plano?.nome || planosPorId[a?.idPlano]?.nome || 'Sem plano';
    const ativasPorPlano = paraLista(contarPor(ativas, nomePlano));
    const receitaPorPlano = Object.entries(
        ativas.reduce((acc, a) => {
            const nome = nomePlano(a);
            acc[nome] = (acc[nome] || 0) + valorMensalAssinatura(a, planosPorId);
            return acc;
        }, {})
    )
        .map(([label, value]) => ({ label, value }))
        .sort((a, b) => b.value - a.value);

    const statusAssinaturas = paraLista(contarPor(assinaturas, (a) => String(a?.status || '').toUpperCase()));
    const metodosPagamento = paraLista(contarPor(ativas, (a) => a?.metodoPagamento), formatarMetodo);
    const periodicidades = paraLista(contarPor(ativas, (a) => a?.periodicidade), formatarPeriodicidade);

    // Evolução mensal (12 meses)
    const meses = ultimosMeses(12, hoje);
    const novasPorMes = contarPorMes(assinaturas, (a) => a?.dataInicio || a?.created_at, meses);
    const cancelamentosPorMes = contarPorMes(cancelamentos, (c) => c?.dataCancelamento, meses);
    const leadsLandingPorMes = contarPorMes(leadsLanding, (l) => l?.createdAt, meses);

    // Janela de 30 dias
    const inicio30 = new Date(hoje.getTime() - 30 * DIA_MS);
    const noPeriodo = (valor) => {
        const d = toDate(valor);
        return d && d >= inicio30 && d <= hoje;
    };
    const cancelamentos30 = cancelamentos.filter((c) => noPeriodo(c?.dataCancelamento)).length;
    const novas30 = assinaturas.filter((a) => noPeriodo(a?.dataInicio || a?.created_at)).length;
    const leadsLanding30 = leadsLanding.filter((l) => noPeriodo(l?.createdAt)).length;
    // Taxa de cancelamento aproximada: cancelados no período / (ativas hoje + cancelados no período)
    const taxaCancelamento30 = ativas.length + cancelamentos30 > 0 ? cancelamentos30 / (ativas.length + cancelamentos30) : 0;

    // Próximas cobranças (7 dias)
    const inicioHoje = new Date(hoje.getFullYear(), hoje.getMonth(), hoje.getDate());
    const fim7 = new Date(inicioHoje.getTime() + 8 * DIA_MS);
    const proximasCobrancas = ativas
        .map((a) => ({ ...a, _data: toDate(a?.proximaCobranca) }))
        .filter((a) => a._data && a._data >= inicioHoje && a._data < fim7)
        .sort((a, b) => a._data - b._data)
        .map((a) => ({
            idAssinatura: a.idAssinatura,
            cliente: a?.usuario?.Nome || 'Cliente',
            plano: nomePlano(a),
            data: a._data,
            valor: valorMensalAssinatura(a, planosPorId) * (String(a?.periodicidade || '').toUpperCase() === 'ANUAL' ? 12 : 1),
        }));
    const valorProximasCobrancas = proximasCobrancas.reduce((acc, c) => acc + c.valor, 0);

    // Motivos de cancelamento
    const motivos = {};
    cancelamentos.forEach((c) => {
        const lista = Array.isArray(c?.motivosSelecionados) ? c.motivosSelecionados : [];
        lista.forEach((m) => { motivos[m] = (motivos[m] || 0) + 1; });
        if (lista.length === 0 && c?.outroMotivo) motivos.Outro = (motivos.Outro || 0) + 1;
    });
    const motivosCancelamento = paraLista(motivos);

    // Leads que não concluíram o cadastro, por etapa
    const etapas = contarPor(leadsPerdidos, (l) => l?.stepAtual);
    const leadsPorEtapa = Object.keys(etapas)
        .sort((a, b) => Number(a) - Number(b))
        .map((step) => ({ label: `Etapa ${step}`, value: etapas[step] }));

    // Clientes
    const clientesComEmpresa = clientes.filter((c) => c?.empresa).length;
    const tipoPessoa = paraLista(contarPor(clientes, (c) => String(c?.TipoPessoa || '').toUpperCase()), (t) => ({ PJ: 'Pessoa jurídica', PF: 'Pessoa física', FISICA: 'Pessoa física', JURIDICA: 'Pessoa jurídica' }[t] || t));
    const idsComAssinaturaAtiva = new Set(ativas.map((a) => a.idUsuario));
    const clientesSemAssinatura = clientes.filter((c) => !idsComAssinaturaAtiva.has(c.idUsuario)).length;

    // Importações (obrigações e demonstrações)
    const resumoImportacoes = (lista = []) => {
        const ordenadas = [...lista].sort((a, b) => (toDate(b?.criadoEm) || 0) - (toDate(a?.criadoEm) || 0));
        const ultimos30 = lista.filter((i) => noPeriodo(i?.criadoEm));
        const arquivos30 = ultimos30.reduce((acc, i) => acc + Number(i?.totalArquivos || 0), 0);
        const erros30 = ultimos30.reduce((acc, i) => acc + Number(i?.erros || 0), 0);
        return {
            total: lista.length,
            ultima: ordenadas[0] || null,
            importacoes30: ultimos30.length,
            arquivos30,
            erros30,
            taxaErro30: arquivos30 > 0 ? erros30 / arquivos30 : 0,
        };
    };

    return {
        meses: meses.map((m) => m.label),
        assinaturas: {
            total: assinaturas.length,
            ativas: ativas.length,
            pendentes: pendentes.length,
            novas30,
            novasPorMes,
            ativasPorPlano,
            receitaPorPlano,
            status: statusAssinaturas,
            metodosPagamento,
            periodicidades,
            proximasCobrancas,
            valorProximasCobrancas,
        },
        receita: { mrr, ticketMedio, arr: mrr * 12 },
        cancelamentos: {
            total: cancelamentos.length,
            ultimos30: cancelamentos30,
            taxa30: taxaCancelamento30,
            porMes: cancelamentosPorMes,
            motivos: motivosCancelamento,
        },
        clientes: {
            total: clientes.length,
            comEmpresa: clientesComEmpresa,
            semAssinaturaAtiva: clientesSemAssinatura,
            tipoPessoa,
        },
        leads: {
            landingTotal: leadsLanding.length,
            landing30: leadsLanding30,
            landingPorMes: leadsLandingPorMes,
            perdidos: leadsPerdidos.length,
            porEtapa: leadsPorEtapa,
        },
        importacoes: {
            guias: resumoImportacoes(dados.importacoesGuias),
            demonstracoes: resumoImportacoes(dados.importacoesDemonstracoes),
        },
    };
}
